import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Gauge as GaugeIcon } from "lucide-react";
import { analysesApi } from "@/lib/api";
import KpiCard from "@/components/KpiCard";
import { Slider } from "@/components/ui/slider";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";

export default function Simulator() {
  const { projectId } = useParams();
  const [rateShock, setRateShock] = useState(0);
  const [addDebt, setAddDebt] = useState(0);
  const [growthShock, setGrowthShock] = useState(0);
  const [sim, setSim] = useState(null);
  const [baseline, setBaseline] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    analysesApi.summary(projectId).then((d) => setBaseline(d));
  }, [projectId]);

  const run = async () => {
    setLoading(true);
    try {
      const r = await analysesApi.simulate(projectId, {
        interest_rate_shock: rateShock,
        additional_debt: addDebt,
        gdp_growth_shock: growthShock,
      });
      setSim(r);
    } finally { setLoading(false); }
  };

  useEffect(() => { if (baseline) run(); /* eslint-disable-next-line */ }, [rateShock, addDebt, growthShock, baseline]);

  if (!baseline) return <div className="p-10 label-mono text-xs text-muted-foreground">Chargement…</div>;

  const b = baseline.macro.viabilite || {};
  const bv = baseline.vitaliste || {};

  return (
    <div className="px-6 lg:px-10 py-8 max-w-6xl mx-auto w-full" data-testid="simulator-page">
      <div className="label-mono text-[10px] text-muted-foreground">MODULE 8.2</div>
      <h1 className="mt-1 font-serif font-bold text-4xl">Simulateur de scénarios</h1>
      <p className="mt-2 text-muted-foreground text-sm max-w-2xl">
        Modifiez les paramètres en temps réel — aucun appel à l'IA, calculs instantanés.
        Observez l'impact sur la dette/PIB, le service, et les indicateurs vitalistes.
      </p>

      <div className="mt-8 grid md:grid-cols-3 gap-6 border border-border bg-card rounded-sm p-6">
        <SliderControl label="Choc taux d'intérêt" value={rateShock} onChange={setRateShock}
                       min={0} max={5} step={0.1} unit="%" testId="sim-rate-slider" />
        <SliderControl label="Dette additionnelle" value={addDebt} onChange={setAddDebt}
                       min={0} max={50_000_000_000} step={100_000_000} unit="USD"
                       format={(v) => (v / 1e9).toFixed(1) + " B"} testId="sim-debt-slider" />
        <SliderControl label="Choc croissance PIB" value={growthShock} onChange={setGrowthShock}
                       min={-5} max={5} step={0.1} unit="pts" testId="sim-growth-slider" />
      </div>

      <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4">
        <Comp label="DETTE / PIB" base={b.debt_gdp_ratio} next={sim?.new_debt_gdp} unit="%" />
        <Comp label="SERVICE / REC." base={b.service_revenue_ratio} next={sim?.new_service_revenue} unit="%" />
        <Comp label="D2 — jours" base={bv.D2} next={sim?.new_D2} unit="j" />
        <Comp label="V5 — TAVD" base={bv.V5} next={sim?.new_V5} unit="%" />
      </div>

      {sim && (
        <div className="mt-10 border border-marine/40 dark:border-gold/40 bg-marine/5 dark:bg-gold/5 rounded-sm p-6">
          <div className="flex items-start gap-3">
            <GaugeIcon className="text-marine dark:text-gold" />
            <div>
              <div className="font-serif font-bold">Scénario courant</div>
              <p className="mt-2 text-xs text-muted-foreground">
                Nouvelle dette totale : <strong className="font-mono">{fmtMoney(sim.new_total_debt)}</strong> ·
                Nouveau service : <strong className="font-mono">{fmtMoney(sim.new_service)}</strong> ·
                Bande TAVD : <strong className="uppercase">{sim.new_tavd_band}</strong>.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function SliderControl({ label, value, onChange, min, max, step, unit, format, testId }) {
  return (
    <div>
      <Label className="label-mono text-[10px] text-muted-foreground">{label}</Label>
      <div className="mt-2 font-mono text-xl text-marine dark:text-gold">
        {format ? format(value) : value.toFixed(step < 1 ? 1 : 0)} <span className="text-xs text-muted-foreground">{unit}</span>
      </div>
      <Slider value={[value]} min={min} max={max} step={step}
              onValueChange={(v) => onChange(v[0])} className="mt-3" data-testid={testId} />
    </div>
  );
}

function Comp({ label, base, next, unit }) {
  const diff = next != null && base != null ? next - base : null;
  return (
    <div className="border border-border bg-card rounded-sm p-4">
      <div className="label-mono text-[9px] text-muted-foreground">{label}</div>
      <div className="mt-2 font-mono text-xl">
        {base == null ? "—" : base.toFixed(1)} <span className="text-xs text-muted-foreground">{unit}</span>
      </div>
      {next != null && (
        <div className="mt-1 text-sm">
          → <span className={`font-mono ${diff > 0 ? "text-alert" : diff < 0 ? "text-success" : ""}`}>
            {next.toFixed(1)}{unit}
          </span>
          {diff != null && <span className="text-xs text-muted-foreground ml-2">
            ({diff > 0 ? "+" : ""}{diff.toFixed(1)})
          </span>}
        </div>
      )}
    </div>
  );
}

function fmtMoney(n) {
  if (!n) return "—";
  if (n >= 1e9) return (n / 1e9).toFixed(2) + " B";
  if (n >= 1e6) return (n / 1e6).toFixed(2) + " M";
  return n.toFixed(0);
}
